<template>
    <div class="html-editor">
        <div class="editor-header">
            <div class="editor-tabs">
                <v-btn
                    :class="{ active: activeTab === 'edit' }"
                    @click="activeTab = 'edit'"
                    text
                    small
                    class="tab-btn"
                >
                    <v-icon small left>mdi-code-tags</v-icon>
                    Code
                </v-btn>
                <v-btn
                    :class="{ active: activeTab === 'preview' }"
                    @click="activeTab = 'preview'"
                    text
                    small
                    class="tab-btn"
                >
                    <v-icon small left>mdi-eye</v-icon>
                    Preview
                </v-btn>
            </div>
            <v-btn
                @click="showPreviewModal = true"
                text
                small
                color="primary"
                class="preview-btn"
            >
                <v-icon small left>mdi-open-in-new</v-icon>
                Preview Full
            </v-btn>
        </div>

        <!-- Code Editor -->
        <div v-show="activeTab === 'edit'" class="editor-content">
            <textarea
                ref="textarea"
                v-model="code"
                :placeholder="placeholder"
                @input="onInput"
                @keydown="onKeydown"
                class="code-textarea"
                spellcheck="false"
            ></textarea>
        </div>

        <!-- Preview Panel -->
        <div v-show="activeTab === 'preview'" class="preview-content">
            <div v-if="code.trim()" class="preview-html" v-html="code"></div>
            <div v-else class="preview-empty">
                <v-icon large color="grey">mdi-eye-off</v-icon>
                <p>Chưa có nội dung để preview</p>
            </div>
        </div>

        <div class="editor-info">
            <span class="info-text">
                <v-icon small>mdi-code-tags</v-icon>
                HTML Editor - Hỗ trợ Tab và tự động indent
            </span>
        </div>

        <!-- Preview Modal -->
        <v-dialog v-model="showPreviewModal" max-width="90%" scrollable>
            <v-card>
                <v-card-title class="d-flex justify-space-between align-center">
                    <span>HTML Preview</span>
                    <v-btn icon @click="showPreviewModal = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>
                <v-card-text class="pa-0">
                    <div class="preview-modal-content">
                        <div v-if="code.trim()" class="preview-html" v-html="code"></div>
                        <div v-else class="preview-empty">
                            <v-icon large color="grey">mdi-eye-off</v-icon>
                            <p>Chưa có nội dung để preview</p>
                        </div>
                    </div>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" @click="showPreviewModal = false">
                        Đóng
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
export default {
    name: 'HtmlEditor',
    props: {
        modelValue: {
            type: String,
            default: ''
        },
        height: {
            type: String,
            default: '300px'
        },
        placeholder: {
            type: String,
            default: 'Nhập code HTML tại đây...'
        }
    },
    data() {
        return {
            code: this.modelValue,
            activeTab: 'edit',
            showPreviewModal: false
        };
    },
    methods: {
        onInput(event) {
            this.code = event.target.value;
            this.$emit('update:modelValue', this.code);
        },
        onKeydown(event) {
            // Xử lý Tab key
            if (event.key === 'Tab') {
                event.preventDefault();
                const textarea = this.$refs.textarea;
                const start = textarea.selectionStart;
                const end = textarea.selectionEnd;
                
                // Thêm tab (2 spaces)
                const spaces = '  ';
                textarea.value = textarea.value.substring(0, start) + spaces + textarea.value.substring(end);
                
                // Đặt con trở về vị trí sau tab
                textarea.selectionStart = textarea.selectionEnd = start + spaces.length;
                
                // Emit change
                this.code = textarea.value;
                this.$emit('update:modelValue', this.code);
            }
            
            // Auto-indent cho Enter
            if (event.key === 'Enter') {
                const textarea = this.$refs.textarea;
                const start = textarea.selectionStart;
                const lines = textarea.value.substring(0, start).split('\n');
                const currentLine = lines[lines.length - 1];
                
                // Đếm số spaces ở đầu dòng hiện tại
                const match = currentLine.match(/^(\s*)/);
                const indent = match ? match[1] : '';
                
                // Thêm indent tương tự cho dòng mới
                if (indent) {
                    setTimeout(() => {
                        const newStart = textarea.selectionStart;
                        textarea.value = textarea.value.substring(0, newStart) + indent + textarea.value.substring(newStart);
                        textarea.selectionStart = textarea.selectionEnd = newStart + indent.length;
                        
                        this.code = textarea.value;
                        this.$emit('update:modelValue', this.code);
                    }, 0);
                }
            }
        }
    },
    watch: {
        modelValue(newValue) {
            if (newValue !== this.code) {
                this.code = newValue;
            }
        }
    }
};
</script>

<style scoped>
.html-editor {
    border: 1px solid #ddd;
    border-radius: 4px;
    overflow: hidden;
    background-color: #f8f9fa;
}

/* Header với tabs */
.editor-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: #f8f9fa;
    border-bottom: 1px solid #e0e0e0;
    padding: 4px 8px;
}

.editor-tabs {
    display: flex;
    gap: 4px;
}

.tab-btn {
    min-width: 80px !important;
    height: 32px !important;
    text-transform: none !important;
    font-size: 12px !important;
    padding: 0 12px !important;
    border-radius: 4px !important;
    transition: all 0.2s ease !important;
}

.tab-btn.active {
    background-color: #1976d2 !important;
    color: white !important;
}

.tab-btn:not(.active) {
    background-color: transparent !important;
    color: #666 !important;
}

.tab-btn:not(.active):hover {
    background-color: #e3f2fd !important;
    color: #1976d2 !important;
}

.preview-btn {
    text-transform: none !important;
    font-size: 12px !important;
    height: 32px !important;
}

/* Editor content */
.editor-content {
    position: relative;
}

.code-textarea {
    width: 100%;
    min-height: v-bind(height);
    max-height: 500px;
    padding: 12px;
    border: none;
    outline: none;
    font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
    font-size: 14px;
    line-height: 1.5;
    background-color: #263238;
    color: #eeffff;
    resize: vertical;
    tab-size: 2;
    -moz-tab-size: 2;
    white-space: pre-wrap;
    word-wrap: break-word;
}

.code-textarea::placeholder {
    color: #90a4ae;
}

.code-textarea:focus {
    background-color: #1e272c;
    box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.2);
}

/* Preview content */
.preview-content {
    min-height: v-bind(height);
    max-height: 500px;
    overflow-y: auto;
    background-color: #ffffff;
    border: 1px solid #e0e0e0;
}

.preview-html {
    padding: 12px;
    word-wrap: break-word;
    overflow-wrap: break-word;
}

.preview-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: v-bind(height);
    color: #666;
    text-align: center;
}

.preview-empty p {
    margin: 8px 0 0 0;
    font-size: 14px;
}

/* Preview Modal */
.preview-modal-content {
    min-height: 400px;
    max-height: 70vh;
    overflow-y: auto;
    background-color: #ffffff;
}

.preview-modal-content .preview-html {
    padding: 24px;
}

.preview-modal-content .preview-empty {
    height: 400px;
}

/* Editor info */
.editor-info {
    padding: 8px 12px;
    background-color: #f5f5f5;
    border-top: 1px solid #e0e0e0;
}

.info-text {
    font-size: 12px;
    color: #666;
    display: flex;
    align-items: center;
    gap: 4px;
}

/* Scrollbar styling */
.code-textarea::-webkit-scrollbar,
.preview-content::-webkit-scrollbar,
.preview-modal-content::-webkit-scrollbar {
    width: 8px;
}

.code-textarea::-webkit-scrollbar-track {
    background: #37474f;
}

.code-textarea::-webkit-scrollbar-thumb {
    background: #546e7a;
    border-radius: 4px;
}

.code-textarea::-webkit-scrollbar-thumb:hover {
    background: #607d8b;
}

.preview-content::-webkit-scrollbar-track,
.preview-modal-content::-webkit-scrollbar-track {
    background: #f5f5f5;
}

.preview-content::-webkit-scrollbar-thumb,
.preview-modal-content::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 4px;
}

.preview-content::-webkit-scrollbar-thumb:hover,
.preview-modal-content::-webkit-scrollbar-thumb:hover {
    background: #aaa;
}
</style> 